/**
 * TMF React Application
 *
 * Entry point com rotas, autenticação, theming e providers.
 * Todas as rotas protegidas usam o PageLayout com header persistente.
 *
 * @since 2025-12-15
 * @epic INIT-0045-03, INIT-0046
 */

import { Routes, Route, Outlet, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef } from 'react';
import { AuthProvider, ProtectedRoute, useAuth } from '@/contexts/AuthContext';
import { I18nProvider } from '@/contexts/I18nContext';
import { ThemeProvider, useTheme } from '@/theme';
import { BootLoader } from '@/components/ui/BootLoader';
import { Toaster } from '@/components/ui/Sonner';
import { PageLayout, AuthLayout } from '@/components/layout';
import { FragmentModeProvider } from '@/contexts/FragmentModeContext';
import { ConfirmProvider } from '@/contexts/ConfirmContext';
import { FragmentEditorModal } from '@/components/FragmentEditorModal';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToastProvider } from '@/components/ui/Toast';
import { useAutoReport } from '@/hooks/useAutoReport';
import { useClientConfig } from '@/hooks/useClientConfig';
import { getTenantClientCode } from '@/utils/tenantClient';
import { usePortalStore } from '@/store/portalStore';
import { FloatingAiChat } from '@/components/ai/FloatingAiChat';

// Lazy load pages for code splitting
// Home agora renderiza content_home do CMS
const ContentViewHome = lazy(() => import('@pages/content/ContentView'));
const PersonList = lazy(() => import('@pages/person/PersonList'));
const PersonDetail = lazy(() => import('@pages/person/PersonDetail'));
const PersonCreate = lazy(() => import('@pages/person/PersonCreate'));
const PersonUpdate = lazy(() => import('@pages/person/PersonUpdate'));
const NotFound = lazy(() => import('@pages/NotFound'));

// Auth pages
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const LogoutPage = lazy(() => import('@pages/auth/LogoutPage'));
const SSOCallbackPage = lazy(() => import('@pages/auth/SSOCallbackPage'));
const CrossLoginPage = lazy(() => import('@pages/auth/CrossLoginPage'));

// Public Campaign pages (Termo de Adesão)
const PublicCampaignLanding = lazy(() => import('@pages/public/campaign/CampaignLanding'));
const PublicCampaignRegister = lazy(() => import('@pages/public/campaign/CampaignRegister'));
const PublicCampaignLogin = lazy(() => import('@pages/public/campaign/CampaignLogin'));
const PublicCampaignDashboard = lazy(() => import('@pages/public/campaign/CampaignDashboard'));

// Public Portal pages (Conteúdo Público)
const PublicHome = lazy(() => import('@pages/public/portal/PublicHome'));
const PublicCategory = lazy(() => import('@pages/public/portal/PublicCategory'));
const PublicContent = lazy(() => import('@pages/public/portal/PublicContent'));

// Public Changelog
const ChangelogPage = lazy(() => import('@pages/public/changelog/ChangelogPage'));

// Public Landing Page
const LandingPage = lazy(() => import('@pages/public/landing/LandingPage'));


// Product pages
const ProductList = lazy(() => import('@pages/product/ProductList'));
const ProductDetail = lazy(() => import('@pages/product/ProductDetail'));
const ProductCreate = lazy(() => import('@pages/product/ProductCreate'));
const ProductUpdate = lazy(() => import('@pages/product/ProductUpdate'));
const CategoryList = lazy(() => import('@pages/product/CategoryList'));
const BrandList = lazy(() => import('@pages/product/BrandList'));
const FamilyList = lazy(() => import('@pages/product/FamilyList'));
const TypeList = lazy(() => import('@pages/product/TypeList'));
const InventoryPage = lazy(() => import('@pages/product/InventoryPage'));

// Warehouse pages (Inventory)
const WarehouseList = lazy(() => import('@pages/product/WarehouseList'));
const WarehouseDetail = lazy(() => import('@pages/product/WarehouseDetail'));
const WarehouseCreate = lazy(() => import('@pages/product/WarehouseCreate'));
const WarehouseUpdate = lazy(() => import('@pages/product/WarehouseUpdate'));

// Product Attributes and Images pages
const AttributeList = lazy(() => import('@pages/product/AttributeList'));
const ImageManager = lazy(() => import('@pages/product/ImageManager'));
const ImportImages = lazy(() => import('@pages/product/ImportImages'));
const ExportImages = lazy(() => import('@pages/product/ExportImages'));

// Content pages (CMS)
const ContentList = lazy(() => import('@pages/content/ContentList'));
const ContentDetail = lazy(() => import('@pages/content/ContentDetail'));
const ContentCreate = lazy(() => import('@pages/content/ContentCreate'));
const ContentUpdate = lazy(() => import('@pages/content/ContentUpdate'));
const ContentView = lazy(() => import('@pages/content/ContentView'));
const ContentCategories = lazy(() => import('@pages/content/ContentCategories'));
const WidgetDemo = lazy(() => import('@pages/content/WidgetDemo'));

// Resource pages
const ResourceList = lazy(() => import('@pages/resource/ResourceList'));
const ResourceDetail = lazy(() => import('@pages/resource/ResourceDetail'));
const ResourceCreate = lazy(() => import('@pages/resource/ResourceCreateWizard'));
const ResourceUpdate = lazy(() => import('@pages/resource/ResourceUpdate'));
const ResourceItemList = lazy(() => import('@pages/resource/ResourceItemList'));
const ResourceItemCreate = lazy(() => import('@pages/resource/ResourceItemCreate'));
const ResourceItemDetail = lazy(() => import('@pages/resource/ResourceItemDetail'));
const ResourceItemUpdate = lazy(() => import('@pages/resource/ResourceItemUpdate'));
const ResourceAttrManager = lazy(() => import('@pages/resource/ResourceAttrManager'));
const ResourceAttrList = lazy(() => import('@pages/resource/ResourceAttrList'));
const ResourceMapPage = lazy(() => import('@pages/resource/ResourceMapPage'));
const ResourceMapReport = lazy(() => import('@pages/resource/ResourceMapReport'));
const WorkflowConfigPage = lazy(() => import('@pages/resource/WorkflowConfigPage'));
const ResourceDocsPage = lazy(() => import('@pages/resource/ResourceDocsPage'));
const ResourceDashboard = lazy(() => import('@pages/resource/ResourceDashboard'));
const ResourceCheckins = lazy(() => import('@pages/resource/ResourceCheckins'));
const ResourceUploads = lazy(() => import('@pages/resource/ResourceUploads'));
const ResourceApprovalsCenter = lazy(() => import('@pages/resource/ResourceApprovalsCenter'));

// Segmentation pages
const RegionList = lazy(() => import('@pages/segmentation/RegionList'));
const SegmentList = lazy(() => import('@pages/segmentation/SegmentList'));

// Person Configuration pages (Cadastro menu)
const AccessList = lazy(() => import('@pages/person/AccessList'));
const PersonLoginList = lazy(() => import('@pages/person/PersonLoginList'));
const ClientGroupList = lazy(() => import('@pages/person/ClientGroupList'));
const ClientGroupEdit = lazy(() => import('@pages/person/ClientGroupEdit'));
const HierarchicalOrganizationList = lazy(() => import('@pages/person/HierarchicalOrganizationList'));
const HierarchicalLevelList = lazy(() => import('@pages/person/HierarchicalLevelList'));
const PersonAttributeList = lazy(() => import('@pages/person/PersonAttributeList'));
const PersonAttributeTypeList = lazy(() => import('@pages/person/PersonAttributeTypeList'));
const SystemIntegrationList = lazy(() => import('@pages/person/SystemIntegrationList'));
const PersonSystemIntegrationList = lazy(() => import('@pages/person/PersonSystemIntegrationList'));
const BusinessAreaList = lazy(() => import('@pages/person/BusinessAreaList'));
const CorporationHierarchicalStructureList = lazy(() => import('@pages/person/CorporationHierarchicalStructureList'));
const SendTokenBatch = lazy(() => import('@pages/person/SendTokenBatch'));
const PersonLatLngPage = lazy(() => import('@pages/person/PersonLatLngPage'));
const PersonMapPage = lazy(() => import('@pages/person/PersonMapPage'));
const PersonImageTypeList = lazy(() => import('@pages/person/PersonImageTypeList'));
const PersonRelationshipTypeList = lazy(() => import('@pages/person/PersonRelationshipTypeList'));
const FamilyRelationshipTypeList = lazy(() => import('@pages/person/FamilyRelationshipTypeList'));

// Settings pages (Entity Types)
const AddressTypeList = lazy(() => import('@pages/settings/AddressTypeList'));
const PhoneTypeList = lazy(() => import('@pages/settings/PhoneTypeList'));
const EmailTypeList = lazy(() => import('@pages/settings/EmailTypeList'));
const PersonTypeList = lazy(() => import('@pages/settings/PersonTypeList'));
const ProfilePage = lazy(() => import('@pages/settings/ProfilePage'));
const CacheManager = lazy(() => import('@pages/settings/CacheManager'));

// Theme Settings pages
const ThemeListPage = lazy(() => import('@pages/settings/theme/ThemeListPage'));
const ThemeConfigPage = lazy(() => import('@pages/settings/theme/ThemeConfigPage'));

// Portal Management
const PortalManagementPage = lazy(() => import('@pages/content/portal/PortalManagementPage'));

// Configuration Settings pages
const ConfigurationList = lazy(() => import('@pages/settings/config/ConfigurationList'));

// System Settings pages
const MenuTypeList = lazy(() => import('@pages/settings/MenuTypeList'));
const MenuManagement = lazy(() => import('@pages/settings/MenuManagement'));
const AuthItemList = lazy(() => import('@pages/settings/AuthItemList'));
const CurrencyList = lazy(() => import('@pages/settings/CurrencyList'));
const LanguageList = lazy(() => import('@pages/settings/LanguageList'));
const DatabaseSchemaPage = lazy(() => import('@pages/settings/DatabaseSchemaPage'));

// Storage Config pages
const StorageConfigList = lazy(() => import('@pages/settings/storage/StorageConfigList'));

// System Jobs & Migrations pages
const JobList = lazy(() => import('@pages/settings/JobList'));
const MigrationManagement = lazy(() => import('@pages/settings/MigrationManagement'));

// ETL pages (Data Warehouse)
const EtlList = lazy(() => import('@pages/settings/etl/EtlList'));
const EtlCreate = lazy(() => import('@pages/settings/etl/EtlCreate'));

// Access Profile Settings pages
const AccessProfileList = lazy(() => import('@pages/settings/access/AccessProfileList'));
const AccessProfileForm = lazy(() => import('@pages/settings/access/AccessProfileForm'));
const AccessAssignmentList = lazy(() => import('@pages/settings/access/AccessAssignmentList'));

// Fragment Management page
const FragmentManagement = lazy(() => import('@pages/admin/FragmentManagement'));

// Schema Editor page (Admin)
const SchemaEditorPage = lazy(() => import('@pages/admin/schema-editor'));
const PrivacyDashboard = lazy(() => import('@pages/admin/privacy/PrivacyDashboard').then(m => ({ default: m.PrivacyDashboard })));
const PrivacyRequestList = lazy(() => import('@pages/admin/privacy/PrivacyRequestList').then(m => ({ default: m.PrivacyRequestList })));
const PrivacyRequestCreate = lazy(() => import('@pages/admin/privacy/PrivacyRequestCreate').then(m => ({ default: m.PrivacyRequestCreate })));
const PrivacyRequestDetail = lazy(() => import('@pages/admin/privacy/PrivacyRequestDetail').then(m => ({ default: m.PrivacyRequestDetail })));
const PrivacySubjectSearch = lazy(() => import('@pages/admin/privacy/PrivacySubjectSearch').then(m => ({ default: m.PrivacySubjectSearch })));

// Budget pages (Financial Management)
const BudgetList = lazy(() => import('@pages/budget/BudgetList'));
const BudgetDetail = lazy(() => import('@pages/budget/BudgetDetail'));
const BudgetCreate = lazy(() => import('@pages/budget/BudgetCreateWizard'));
const BudgetUpdate = lazy(() => import('@pages/budget/BudgetUpdate'));
const BudgetManage = lazy(() => import('@pages/budget/BudgetManage'));
const CostCenterList = lazy(() => import('@pages/budget/CostCenterList'));
const CostCenterDetail = lazy(() => import('@pages/budget/CostCenterDetail'));
const CostCenterCreate = lazy(() => import('@pages/budget/CostCenterCreateWizard'));
const CostCenterUpdate = lazy(() => import('@pages/budget/CostCenterUpdate'));
const BudgetApproval = lazy(() => import('@pages/budget/BudgetApproval'));
const BudgetTransactions = lazy(() => import('@pages/budget/BudgetTransactions'));
const BudgetStatement = lazy(() => import('@pages/budget/BudgetStatement'));
const BudgetBalances = lazy(() => import('@pages/budget/BudgetBalances'));
const CostCenterBalance = lazy(() => import('@pages/budget/CostCenterBalance'));
const ActionList = lazy(() => import('@pages/budget/ActionList'));
const ActionForm = lazy(() => import('@pages/budget/ActionFormWizard'));

// Finance pages (DRE, Financial Categories)
const FinanceCategoryList = lazy(() => import('@pages/finance/FinanceCategoryList'));
const FinanceCategoryForm = lazy(() => import('@pages/finance/FinanceCategoryForm'));
const FinanceDRE = lazy(() => import('@pages/finance/FinanceDRE'));

// Store pages (E-commerce)
const StoreListPage = lazy(() => import('@pages/store/StoreListPage'));
const StoreDetailPage = lazy(() => import('@pages/store/StoreDetailPage'));
const StoreCreatePage = lazy(() => import('@pages/store/StoreCreatePage'));
const StoreEditPage = lazy(() => import('@pages/store/StoreEditPage'));
const CatalogPage = lazy(() => import('@pages/store/CatalogPage'));
const CartPage = lazy(() => import('@pages/store/CartPage'));
const CheckoutPage = lazy(() => import('@pages/store/CheckoutPage'));
const SalesListPage = lazy(() => import('@pages/store/SalesListPage'));
const SaleDetailPage = lazy(() => import('@pages/store/SaleDetailPage'));
const PaymentMethodsPage = lazy(() => import('@pages/store/PaymentMethodsPage'));
const ShipmentMethodsPage = lazy(() => import('@pages/store/ShipmentMethodsPage'));
const TrackingPage = lazy(() => import('@pages/store/TrackingPage'));

// Campaign pages (Marketing Campaigns)
const CampaignList = lazy(() => import('@pages/campaign/CampaignList'));
const CampaignDetail = lazy(() => import('@pages/campaign/CampaignDetail'));
const CampaignCreate = lazy(() => import('@pages/campaign/CampaignCreate'));
const CampaignUpdate = lazy(() => import('@pages/campaign/CampaignUpdate'));
const CampaignDashboard = lazy(() => import('@pages/campaign/CampaignDashboard'));
const CampaignAttributes = lazy(() => import('@pages/campaign/CampaignAttributes'));
const CampaignAttributeTypes = lazy(() => import('@pages/campaign/CampaignAttributeTypes'));
const CampaignAttributeOptions = lazy(() => import('@pages/campaign/CampaignAttributeOptions'));
const CampaignAttributeTypeForm = lazy(() => import('@pages/campaign/CampaignAttributeTypeForm'));
const CampaignAttributeForm = lazy(() => import('@pages/campaign/CampaignAttributeForm'));
const CampaignJobs = lazy(() => import('@pages/campaign/CampaignJobs'));
const CampaignImportJobs = lazy(() => import('@pages/campaign/CampaignImportJobs'));
const CampaignTerms = lazy(() => import('@pages/campaign/CampaignTerms'));
const CampaignResults = lazy(() => import('@pages/campaign/CampaignResults'));
const CampaignCredits = lazy(() => import('@pages/campaign/CampaignCredits'));
const MembershipList = lazy(() => import('@pages/campaign/MembershipList'));
const MembershipCreate = lazy(() => import('@pages/campaign/MembershipCreate'));
const CampaignModalities = lazy(() => import('@pages/campaign/CampaignModalities'));
const CampaignModalityForm = lazy(() => import('@pages/campaign/CampaignModalityForm'));
const CampaignPerformanceConfig = lazy(() => import('@pages/campaign/CampaignPerformanceConfig'));
const CampaignPerformanceResults = lazy(() => import('@pages/campaign/CampaignPerformanceResults'));
const CampaignPerformanceDashboard = lazy(() => import('@pages/campaign/CampaignPerformanceDashboard'));
const ScoreLotReversal = lazy(() => import('@pages/campaign/ScoreLotReversal'));
const ScoreCreditGeneration = lazy(() => import('@pages/campaign/ScoreCreditGeneration'));
const ScoreResultGeneration = lazy(() => import('@pages/campaign/ScoreResultGeneration'));
const CampaignWizard = lazy(() => import('@pages/campaign/wizard/CampaignWizard'));

// Ranking pages
const RankingListPage = lazy(() => import('@pages/ranking/RankingListPage'));
const RankingDetailPage = lazy(() => import('@pages/ranking/RankingDetailPage'));
const RankingCreatePage = lazy(() => import('@pages/ranking/RankingCreatePage'));
const RankingEditPage = lazy(() => import('@pages/ranking/RankingEditPage'));
const RankingGenerate = lazy(() => import('@pages/ranking/RankingGenerate'));
const RankingJobs = lazy(() => import('@pages/ranking/RankingJobs'));

// Reports pages
// Analytics pages (Superset)
const SupersetPage = lazy(() => import('@pages/analytics/SupersetPage'));

const ReportsDashboard = lazy(() => import('@pages/reports/ReportsDashboard'));
const ReportsList = lazy(() => import('@pages/reports/ReportsList'));
const ReportBuilder = lazy(() => import('@pages/reports/ReportBuilder'));
const ReportView = lazy(() => import('@pages/reports/ReportView'));
const AccessLogReport = lazy(() => import('@pages/reports/AccessLogReport'));
const ContentAccessReport = lazy(() => import('@pages/reports/ContentAccessReport'));
const AuditLogReport = lazy(() => import('@pages/reports/AuditLogReport'));
const CampaignDataReport = lazy(() => import('@pages/reports/CampaignDataReport'));
const CampaignExportReport = lazy(() => import('@pages/reports/CampaignExportReport'));
const ParticipantsReport = lazy(() => import('@pages/reports/ParticipantsReport'));
const SalesReport = lazy(() => import('@pages/reports/SalesReport'));
const PersonDataReport = lazy(() => import('@pages/reports/PersonDataReport'));
const PersonHierarchyReport = lazy(() => import('@pages/reports/PersonHierarchyReport'));
const PersonSegmentsReport = lazy(() => import('@pages/reports/PersonSegmentsReport'));
const ProductsReport = lazy(() => import('@pages/reports/ProductsReport'));
const InventoryReport = lazy(() => import('@pages/reports/InventoryReport'));
const ProductSalesReport = lazy(() => import('@pages/reports/ProductSalesReport'));
const BudgetReportPage = lazy(() => import('@pages/reports/BudgetReport'));
const CostCentersReport = lazy(() => import('@pages/reports/CostCentersReport'));
const TransactionsReport = lazy(() => import('@pages/reports/TransactionsReport'));

// Quiz pages (Avaliações)
const QuizList = lazy(() => import('@pages/quiz/QuizList'));
const QuizCreate = lazy(() => import('@pages/quiz/QuizCreate'));
const QuizDetail = lazy(() => import('@pages/quiz/QuizDetail'));
const QuizUpdate = lazy(() => import('@pages/quiz/QuizUpdate'));
const QuizConfig = lazy(() => import('@pages/quiz/QuizConfig'));
const QuizExecutions = lazy(() => import('@pages/quiz/QuizExecutions'));
const QuizAnswers = lazy(() => import('@pages/quiz/QuizAnswers'));
const QuizAnswer = lazy(() => import('@pages/quiz/QuizAnswer'));
const QuizReports = lazy(() => import('@pages/quiz/QuizReports'));
const QuizAnswersReport = lazy(() => import('@pages/quiz/QuizAnswersReport'));
const QuizPersonReport = lazy(() => import('@pages/quiz/QuizPersonReport'));

// Function pages (Funções serverless)
const FunctionList = lazy(() => import('@pages/function/FunctionList'));
const FunctionDetail = lazy(() => import('@pages/function/FunctionDetail'));
const FunctionCreate = lazy(() => import('@pages/function/FunctionCreate'));

// Import pages (Importações)
const ImportDashboard = lazy(() => import('@pages/import/ImportDashboard'));
const ImportUpload = lazy(() => import('@pages/import/ImportUpload'));
const ImportFiles = lazy(() => import('@pages/import/ImportFiles'));
const ImportLogs = lazy(() => import('@pages/import/ImportLogs'));
const ImportTypes = lazy(() => import('@pages/import/ImportTypes'));
const ImportTypeCreate = lazy(() => import('@pages/import/ImportTypeCreate'));
const ImportTypeUpdate = lazy(() => import('@pages/import/ImportTypeUpdate'));
const ImportValidations = lazy(() => import('@pages/import/ImportValidations'));
const ImportValidationCreate = lazy(() => import('@pages/import/ImportValidationCreate'));
const ImportValidationUpdate = lazy(() => import('@pages/import/ImportValidationUpdate'));
const ImportValidationDetail = lazy(() => import('@pages/import/ImportValidationDetail'));
const ImportConversions = lazy(() => import('@pages/import/ImportConversions'));
const ImportConversionCreate = lazy(() => import('@pages/import/ImportConversionCreate'));
const ImportConversionUpdate = lazy(() => import('@pages/import/ImportConversionUpdate'));
const ImportFields = lazy(() => import('@pages/import/ImportFields'));
const ImportFieldCreate = lazy(() => import('@pages/import/ImportFieldCreate'));
const ImportFieldUpdate = lazy(() => import('@pages/import/ImportFieldUpdate'));
const ImportConfigCreate = lazy(() => import('@pages/import/ImportConfigCreate'));

// Drawing pages (Sorteios)
const DrawingList = lazy(() => import('@pages/drawing/DrawingList'));
const DrawingResults = lazy(() => import('@pages/drawing/DrawingResults'));

// Legacy module placeholder
const LegacyModulePage = lazy(() => import('@pages/common/LegacyModulePage'));

// Checkin pages (Mapas e visualizações)
const CheckinMapPage = lazy(() => import('@pages/checkin/CheckinMapPage'));
const CheckinPlanPage = lazy(() => import('@pages/checkin/PlanPage'));

// SAC (Central de Atendimento) — casca única que consome o motor SD via @tmf/service-desk-front.
// Superfícies legadas locais (SacChatPage/SACReportsPage/proxy Hub) removidas: o pacote provê
// chat/relatórios; /sac/* redireciona para /service-desk.
const ServiceDeskPage = lazy(() => import('@pages/sac/ServiceDeskPage'));

// Contact pages removidas (TH-56761): motor tmf_contact deletado — rotas legadas redirecionam para /service-desk

const PushSettingsPage = lazy(() => import('@pages/settings/PushSettingsPage'));
const VersionSettingsPage = lazy(() => import('@pages/settings/VersionSettingsPage'));

// Marketing pages (Social Posts, YouTube, Calendar)
const SocialPostPage = lazy(() => import('@pages/marketing/SocialPostPage'));
const YouTubeManager = lazy(() => import('@pages/marketing/YouTubeManager'));
const EditorialCalendar = lazy(() => import('@pages/marketing/EditorialCalendar'));

// Smart Inbox pages (Upload inteligente de documentos)
const SmartInboxReviewPage = lazy(() => import('@pages/smart-inbox/SmartInboxReviewPage'));

// Blog pages (Editor WYSIWYG)
const BlogEditorPage = lazy(() => import('@pages/blog/BlogEditorPage'));

// Test pages (Development)
const ChartTestPage = lazy(() => import('@pages/test/ChartTestPage'));

// Design System Catalog (DEV only)
const DesignSystemCatalog = lazy(() => import('@pages/design-system/DesignSystemCatalog'));

// Docs pages (Markdown Notebook)
const DocsPage = lazy(() => import('@pages/docs/DocsPage'));
const DocsContentPage = lazy(() => import('@pages/docs/DocsContentPage'));

// File Manager pages
const FilesPage = lazy(() => import('@pages/files/FilesPage'));

// Organization pages (Estrutura Organizacional)
const CompanyOrganizationPage = lazy(() => import('@pages/organization/CompanyOrganizationPage'));
const OrganogramPage = lazy(() => import('@pages/organization/OrganogramPage'));

// API Documentation Portal
const ApiDocsPage = lazy(() => import('@pages/api-docs/ApiDocsPage'));

// Hub do Cliente pages
const HubDashboard = lazy(() => import('@pages/hub/HubDashboard'));
const HubDemandList = lazy(() => import('@pages/hub/HubDemandList'));
const HubTicketNew = lazy(() => import('@pages/hub/HubTicketNew'));
const HubDemandDetail = lazy(() => import('@pages/hub/HubDemandDetail'));
const HubInvoiceList = lazy(() => import('@pages/hub/HubInvoiceList'));
const HubContractList = lazy(() => import('@pages/hub/HubContractList'));
const HubHoursList = lazy(() => import('@pages/hub/HubHoursList'));
const HubConsumption = lazy(() => import('@pages/hub/HubConsumption'));
const HubDocuments = lazy(() => import('@pages/hub/HubDocuments'));
const HubTicketMetrics = lazy(() => import('@pages/hub/HubTicketMetrics'));
const CommunicationPage = lazy(() => import('@pages/communication/CommunicationPage'));

// ==========================================
// CLIENT ROUTES (OVERLAY)
// ==========================================

/**
 * Carrega rotas do cliente overlay, se disponível.
 *
 * Cada cliente pode criar src/clients/{clientCode}/routes.tsx
 * exportando clientRoutes (JSX com Route elements).
 *
 * Detecção de cliente extraída para utils/tenantClient.ts (TMF9-58844) —
 * reutilizada pelo gating das rotas /sac/* (proxy Hub, só tenant copa).
 */
function loadClientRoutes(): React.ReactNode | null {
  const clientCode = getTenantClientCode();
  if (!clientCode) return null;

  try {
    const clientModules = import.meta.glob(
      '/src/clients/*/routes.tsx',
      { eager: true }
    ) as Record<string, { suvinilRoutes?: React.ReactNode; clientRoutes?: React.ReactNode }>;

    const modulePath = `/src/clients/${clientCode}/routes.tsx`;
    const clientModule = clientModules[modulePath];

    if (clientModule) {
      // Suporta exports nomeados por convenção ou genérico
      return clientModule.clientRoutes
        || clientModule[`${clientCode}Routes` as keyof typeof clientModule] as React.ReactNode
        || null;
    }
  } catch {
    // Cliente sem routes - comportamento normal
  }

  return null;
}

const clientRouteElements = loadClientRoutes();

// Loading do boot — mesma tela usada por ThemeProvider/ProtectedRoute, para que
// a troca de estágio não pisque [TMF9-61077].
const PageLoader = () => <BootLoader />;

// Layout wrapper para rotas protegidas
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <PageLayout showSidebar>
        <Outlet />
      </PageLayout>
      <FloatingAiChat />
    </ProtectedRoute>
  );
}

// Layout wrapper para rotas de autenticação
function AuthLayoutWrapper() {
  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
}

// Redirect /stores/:id -> /store/:id (preserva o ID)
function StoresIdRedirect({ suffix = '' }: { suffix?: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/store/${id}${suffix}`} replace />;
}

// RootRedirect: redireciona "/" baseado em portal_enabled e autenticação
function RootRedirect() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { portalEnabled, isLoaded, load } = usePortalStore();

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading || !isLoaded) return <PageLoader />;

  if (portalEnabled && !isAuthenticated) {
    return <Navigate to="/portal" replace />;
  }

  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <PageLayout showSidebar>
          <ContentViewHome code="content_home" />
        </PageLayout>
      </ProtectedRoute>
    );
  }

  return <Navigate to="/login" replace />;
}

/** Ativa auto-report de erros em produção */
function AutoReportProvider({ children }: { children: React.ReactNode }) {
  useAutoReport();
  return <>{children}</>;
}

/** Pre-fetches client config from Hub (only when authenticated). */
function ClientConfigInitializer({ children }: { children: React.ReactNode }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('tmf_token') : null;
  // Only fetch config when user is authenticated (has token)
  useClientConfig({ enabled: !!token });
  return <>{children}</>;
}

/**
 * Recarrega o tema quando a sessão passa a existir [TMF9-61077].
 *
 * `GET /themes/default` é autenticado, e o ThemeProvider busca o tema uma única
 * vez no mount — montado ACIMA do AuthProvider, ele roda antes de haver sessão e
 * leva 401, caindo no tema default. Como o login é SPA (setUser + navigate, sem
 * reload), o provider nunca remonta e o tema do tenant só apareceria após F5.
 *
 * Este componente vive DENTRO do AuthProvider justamente para enxergar
 * `isAuthenticated` e disparar o refetch na transição deslogado → logado.
 */
export function ThemeAuthSync({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { refreshTheme } = useTheme();
  // Marca a sessão já sincronizada: evita refetch a cada re-render do provider.
  const syncedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      // Logout: libera para buscar de novo no próximo login (tenant pode mudar).
      syncedRef.current = false;
      return;
    }

    if (syncedRef.current) return;
    syncedRef.current = true;
    void refreshTheme();
  }, [isAuthenticated, authLoading, refreshTheme]);

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <ToastProvider>
      <I18nProvider preloadCategories={['common', 'buttons', 'errors', 'validation']}>
        <Toaster position="top-right" richColors />
        <FragmentModeProvider>
          <ConfirmProvider>
            <AuthProvider>
              <ConfirmProvider>
              <ThemeAuthSync>
              <ClientConfigInitializer>
              {/* FragmentEditorModal: modal global para editar fragments no Discovery Mode */}
              <FragmentEditorModal />
              <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* ============================================= */}
              {/* PUBLIC ROUTES - Sem autenticação             */}
              {/* ============================================= */}

              {/* Root route - redireciona baseado em portal_enabled */}
              <Route path="/" element={<RootRedirect />} />

              {/* Public Changelog */}
              <Route path="/changelog" element={<ChangelogPage />} />

              {/* Public Landing Page */}
              <Route path="/landing" element={<LandingPage />} />

              {/* Public Portal routes (Conteúdo Público) */}
              <Route path="/portal" element={<PublicHome />} />
              <Route path="/portal/category/:code" element={<PublicCategory />} />
              <Route path="/portal/content/:slug" element={<PublicContent />} />

              {/* Public Campaign routes (Termo de Adesão) */}
              <Route path="/c/:slug" element={<PublicCampaignLanding />} />
              <Route path="/c/:slug/register" element={<PublicCampaignRegister />} />
              <Route path="/c/:slug/login" element={<PublicCampaignLogin />} />
              <Route path="/c/:slug/dashboard" element={<PublicCampaignDashboard />} />

              {/* Auth routes (public) - sem header */}
              <Route element={<AuthLayoutWrapper />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LogoutPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />
                <Route path="/cross-login" element={<CrossLoginPage />} />
              </Route>

              {/* Protected routes - com header persistente */}
              <Route element={<ProtectedLayout />}>
                {/* Person routes */}
                <Route path="/person" element={<PersonList />} />
                <Route path="/person/create" element={<PersonCreate />} />
                <Route path="/person/:id" element={<PersonDetail />} />
                <Route path="/person/:id/edit" element={<PersonUpdate />} />

                {/* Product routes */}
                <Route path="/product" element={<ProductList />} />
                <Route path="/product/create" element={<ProductCreate />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/product/:id/edit" element={<ProductUpdate />} />
                <Route path="/product/categories" element={<CategoryList />} />
                <Route path="/product/brands" element={<BrandList />} />
                <Route path="/product/families" element={<FamilyList />} />
                <Route path="/product/types" element={<TypeList />} />
                <Route path="/product/inventory" element={<InventoryPage />} />
                <Route path="/product/attributes" element={<AttributeList />} />
                <Route path="/product/images" element={<ImageManager />} />
                <Route path="/product/import-images" element={<ImportImages />} />
                <Route path="/product/export-images" element={<ExportImages />} />

                {/* Warehouse routes (Inventory) */}
                <Route path="/product/warehouses" element={<WarehouseList />} />
                <Route path="/product/warehouses/create" element={<WarehouseCreate />} />
                <Route path="/product/warehouses/:id" element={<WarehouseDetail />} />
                <Route path="/product/warehouses/:id/edit" element={<WarehouseUpdate />} />

                {/* Content routes (CMS) */}
                <Route path="/content" element={<ContentList />} />
                <Route path="/content/create" element={<ContentCreate />} />
                <Route path="/content/categories" element={<ContentCategories />} />
                {/* ContentDetail detecta se :id é numérico. Se não, redireciona para /page/:code */}
                <Route path="/content/:id" element={<ContentDetail />} />
                <Route path="/content/:id/edit" element={<ContentUpdate />} />

                {/* Blog editor */}
                <Route path="/blog/new" element={<BlogEditorPage />} />
                <Route path="/blog/:id/edit" element={<BlogEditorPage />} />

                {/* Public content pages (by code/slug) */}
                <Route path="/page/menus" element={<Navigate to="/settings/menus" replace />} />
                <Route path="/page/:code" element={<ContentView />} />

                {/* Widget Demo page */}
                <Route path="/widget-demo" element={<WidgetDemo />} />

                {/* Resource routes */}
                <Route path="/resource" element={<ResourceList />} />
                <Route path="/resource/list" element={<Navigate to="/resource" replace />} />
                <Route path="/merchandising" element={<Navigate to="/resource" replace />} />
                <Route path="/resource/create" element={<ResourceCreate />} />
                <Route path="/resource/dashboard" element={<ResourceDashboard />} />
                <Route path="/resource/checkins" element={<ResourceCheckins />} />
                <Route path="/resource/approvals" element={<ResourceApprovalsCenter />} />
                <Route path="/resource/uploads" element={<ResourceUploads />} />
                <Route path="/resource/attributes" element={<ResourceAttrList />} />
                <Route path="/resource/map-report" element={<ResourceMapReport />} />
                <Route path="/resource/map" element={<ResourceMapPage />} />
                {/* Resource items by code - DEVE vir ANTES de /resource/:id para não ser capturado */}
                <Route path="/resource/items/:code" element={<ResourceItemList />} />
                <Route path="/resource/:id" element={<ResourceDetail />} />
                <Route path="/resource/:id/edit" element={<ResourceUpdate />} />
                <Route path="/resource/:id/items" element={<ResourceItemList />} />
                <Route path="/resource/:id/item/create" element={<ResourceItemCreate />} />
                <Route path="/resource/:id/item/:itemId" element={<ResourceItemDetail />} />
                <Route path="/resource/:id/item/:itemId/edit" element={<ResourceItemUpdate />} />
                <Route path="/resource/:id/attrs" element={<ResourceAttrManager />} />
                <Route path="/resource/:id/map" element={<ResourceMapPage />} />
                <Route path="/resource/:id/workflow" element={<WorkflowConfigPage />} />
                <Route path="/resource/:id/docs" element={<ResourceDocsPage />} />

                {/* Alias direto para /leads -> lista de items do resource "leads" */}
                <Route path="/leads" element={<Navigate to="/resource/items/leads" replace />} />

                {/* Segmentation routes */}
                <Route path="/segmentation/regions" element={<RegionList />} />
                <Route path="/segmentation/segments" element={<SegmentList />} />

                {/* Person Configuration routes (Cadastro menu) */}
                <Route path="/person/access" element={<AccessList />} />
                <Route path="/person/logins" element={<PersonLoginList />} />
                <Route path="/person/client-groups" element={<ClientGroupList />} />
                <Route path="/person/hierarchical-org" element={<HierarchicalOrganizationList />} />
                <Route path="/person/hierarchical-levels" element={<HierarchicalLevelList />} />
                <Route path="/person/attributes" element={<PersonAttributeList />} />
                <Route path="/person/attribute-types" element={<PersonAttributeTypeList />} />
                <Route path="/person/system-integrations" element={<SystemIntegrationList />} />
                <Route path="/person/person-integrations" element={<PersonSystemIntegrationList />} />
                <Route path="/person/business-areas" element={<BusinessAreaList />} />
                <Route path="/person/corporation-structures" element={<CorporationHierarchicalStructureList />} />
                <Route path="/person/send-token-batch" element={<SendTokenBatch />} />
                <Route path="/person/latlng" element={<PersonLatLngPage />} />
                <Route path="/person/map" element={<PersonMapPage />} />
                <Route path="/person/image-types" element={<PersonImageTypeList />} />
                <Route path="/person/relationship-types" element={<PersonRelationshipTypeList />} />
                <Route path="/person/family-relationship-types" element={<FamilyRelationshipTypeList />} />

                {/* Client Group Edit route */}
                <Route path="/person/client-groups/:id/edit" element={<ClientGroupEdit />} />

                {/* Legacy Yii URLs for Cadastro (direct routes, no redirects) */}
                <Route path="/ManageAcess" element={<AccessList />} />
                <Route path="/ManageClientGroup" element={<ClientGroupList />} />
                <Route path="/ManageClientGroup/edit/:id" element={<ClientGroupEdit />} />
                <Route path="/ManageHierarchicalOrganization" element={<HierarchicalOrganizationList />} />
                <Route path="/ManageHierarchicalLevel" element={<HierarchicalLevelList />} />
                <Route path="/ManageAttributePeople" element={<PersonAttributeList />} />
                <Route path="/ManageAttributePersonType" element={<PersonAttributeTypeList />} />
                <Route path="/ManageSystemIntegration" element={<SystemIntegrationList />} />
                <Route path="/ManagePersonSystemIntegration" element={<PersonSystemIntegrationList />} />
                <Route path="/ManageBusinessArea" element={<BusinessAreaList />} />
                <Route path="/ManageCorporationHierarchicalStructures" element={<CorporationHierarchicalStructureList />} />
                <Route path="/organization/company" element={<CompanyOrganizationPage />} />
                <Route path="/organization/company/:corporationId" element={<CompanyOrganizationPage />} />
                <Route path="/organization/organogram" element={<OrganogramPage />} />
                <Route path="/sendTokenLot" element={<SendTokenBatch />} />
                <Route path="/ManagePersonLatLng" element={<PersonLatLngPage />} />
                <Route path="/ManageImagePerson" element={<PersonImageTypeList />} />
                <Route path="/ManageRelationshipTypePerson" element={<PersonRelationshipTypeList />} />
                <Route path="/ManageRelationshipFamily" element={<FamilyRelationshipTypeList />} />

                {/* Settings routes (Entity Types) */}
                <Route path="/settings/address-types" element={<AddressTypeList />} />
                <Route path="/settings/phone-types" element={<PhoneTypeList />} />
                <Route path="/settings/email-types" element={<EmailTypeList />} />
                <Route path="/settings/person-types" element={<PersonTypeList />} />

                {/* Cache management */}
                <Route path="/settings/cache" element={<CacheManager />} />

                {/* Profile route */}
                <Route path="/profile" element={<ProfilePage />} />

                {/* Theme Settings routes */}
                <Route path="/settings/themes" element={<ThemeListPage />} />
                <Route path="/settings/themes/:id" element={<ThemeConfigPage />} />

                {/* Portal Management */}
                <Route path="/content/portal" element={<PortalManagementPage />} />
                <Route path="/ManageThemesConfig" element={<Navigate to="/settings/themes" replace />} />

                {/* Configuration Settings routes */}
                <Route path="/settings/configurations" element={<ConfigurationList />} />
                <Route path="/CustomConfigurations" element={<Navigate to="/settings/configurations" replace />} />

                {/* System Jobs & Migrations */}
                <Route path="/settings/jobs" element={<JobList />} />
                <Route path="/settings/migrations" element={<MigrationManagement />} />

                {/* Push (Mensagens) e Versão [TMF9-56029, antes TMF9-53666] */}
                <Route path="/settings/push" element={<PushSettingsPage />} />
                <Route path="/settings/version" element={<VersionSettingsPage />} />

                {/* System Settings routes */}
                <Route path="/settings/etl" element={<EtlList />} />
                <Route path="/settings/etl/create" element={<EtlCreate />} />
                <Route path="/settings/etl/:id" element={<EtlList />} />
                <Route path="/settings/etl/:id/edit" element={<EtlCreate />} />

                <Route path="/settings/menu-types" element={<MenuTypeList />} />
                <Route path="/settings/menus" element={<MenuManagement />} />
                <Route path="/ManageMenu" element={<MenuManagement />} />
                <Route path="/settings/auth-items" element={<AuthItemList />} />
                <Route path="/settings/currencies" element={<CurrencyList />} />
                <Route path="/settings/storage-configs" element={<StorageConfigList />} />
                <Route path="/settings/languages" element={<LanguageList />} />
                <Route path="/gerenciar_idiomas" element={<LanguageList />} />
                {/* Email Logs redirect: settings → communication */}
                <Route path="/settings/email-logs" element={<Navigate to="/" replace />} />
                <Route path="/settings/database-schema" element={<DatabaseSchemaPage />} />

                {/* Access Profile Settings routes */}
                <Route path="/settings/access/profiles" element={<AccessProfileList />} />
                <Route path="/settings/access/profiles/create" element={<AccessProfileForm />} />
                <Route path="/settings/access/profiles/:name/edit" element={<AccessProfileForm />} />
                <Route path="/settings/access/assignments" element={<AccessAssignmentList />} />

                {/* Legacy Yii URL for Access Profiles */}
                <Route path="/ManageAccessProfile" element={<AccessProfileList />} />
                <Route path="/ManageAccess" element={<AccessAssignmentList />} />

                {/* Fragment Management route */}
                <Route path="/settings/fragments" element={<FragmentManagement />} />

                {/* API Keys & Documentation */}
                <Route path="/settings/api-keys" element={<ApiDocsPage />} />

                {/* Schema Editor routes (Admin) */}
                <Route path="/admin/schema-editor" element={<SchemaEditorPage />} />
                <Route path="/admin/schema-editor/:resourceCode" element={<SchemaEditorPage />} />

                {/* Privacy / LGPD (DPO) */}
                <Route path="/admin/privacy" element={<PrivacyDashboard />} />
                <Route path="/admin/privacy/requests" element={<PrivacyRequestList />} />
                <Route path="/admin/privacy/requests/new" element={<PrivacyRequestCreate />} />
                <Route path="/admin/privacy/requests/:id" element={<PrivacyRequestDetail />} />
                <Route path="/admin/privacy/search" element={<PrivacySubjectSearch />} />

                {/* Budget routes (Financial Management) */}
                <Route path="/budget" element={<BudgetList />} />
                <Route path="/budget/create" element={<BudgetCreate />} />
                <Route path="/budget/approval" element={<BudgetApproval />} />
                <Route path="/budget/transactions" element={<BudgetTransactions />} />
                <Route path="/budget/statement" element={<BudgetStatement />} />
                <Route path="/budget/statement/:costCenterId" element={<BudgetStatement />} />
                {/* Budget Actions routes */}
                <Route path="/budget/actions" element={<ActionList />} />
                <Route path="/budget/actions/create" element={<ActionForm />} />
                <Route path="/budget/actions/:id" element={<ActionForm />} />
                <Route path="/budget/actions/:id/edit" element={<ActionForm />} />
                <Route path="/budget/:id" element={<BudgetDetail />} />
                <Route path="/budget/:id/edit" element={<BudgetUpdate />} />
                <Route path="/budget/:id/manage" element={<BudgetManage />} />
                <Route path="/budget/:id/balances" element={<BudgetBalances />} />
                <Route path="/budget/cost-centers" element={<CostCenterList />} />
                <Route path="/budget/cost-centers/create" element={<CostCenterCreate />} />
                <Route path="/budget/cost-centers/:id" element={<CostCenterDetail />} />
                <Route path="/budget/cost-centers/:id/edit" element={<CostCenterUpdate />} />
                <Route path="/budget/cost-centers/:id/balance" element={<CostCenterBalance />} />
                {/* Cost centers nested under budget */}
                <Route path="/budget/:budgetId/cost-centers" element={<CostCenterList />} />
                <Route path="/budget/:budgetId/cost-centers/create" element={<CostCenterCreate />} />
                <Route path="/budget/:budgetId/cost-centers/:id" element={<CostCenterDetail />} />
                <Route path="/budget/:budgetId/cost-centers/:id/edit" element={<CostCenterUpdate />} />
                <Route path="/budget/:budgetId/cost-centers/:id/balance" element={<CostCenterBalance />} />

                {/* Budget Yii-compatible URLs (direct routes, no redirects) */}
                <Route path="/budget/budget" element={<BudgetList />} />
                <Route path="/budget/budget/admin" element={<BudgetList />} />
                <Route path="/budget/budget/manage" element={<BudgetList />} />
                <Route path="/budget/budgetCostCenter" element={<CostCenterList />} />
                <Route path="/budget/budgetCostCenter/admin" element={<CostCenterList />} />
                <Route path="/budget/budgetCostCenter/listActions" element={<ActionList />} />
                <Route path="/budget/report" element={<BudgetReportPage />} />
                <Route path="/ConfigureBudget" element={<BudgetList />} />
                <Route path="/ManageBudget" element={<BudgetList />} />
                <Route path="/ManageBudgetCostCenter" element={<CostCenterList />} />
                <Route path="/ManageBudgetActions" element={<ActionList />} />

                {/* Finance routes (DRE, Financial Categories) */}
                <Route path="/finance/categories" element={<FinanceCategoryList />} />
                <Route path="/finance/categories/create" element={<FinanceCategoryForm />} />
                <Route path="/finance/categories/dre" element={<FinanceDRE />} />
                <Route path="/finance/categories/:id" element={<FinanceCategoryForm />} />
                <Route path="/finance/categories/:id/edit" element={<FinanceCategoryForm />} />

                {/* Store routes (E-commerce) */}
                <Route path="/store" element={<StoreListPage />} />
                <Route path="/store/create" element={<StoreCreatePage />} />
                <Route path="/store/:id" element={<StoreDetailPage />} />
                <Route path="/store/:id/edit" element={<StoreEditPage />} />
                <Route path="/store/:id/catalog" element={<CatalogPage />} />
                {/* Alias: /stores -> /store (redirect para URL canônica) */}
                <Route path="/stores" element={<Navigate to="/store" replace />} />
                <Route path="/stores/create" element={<Navigate to="/store/create" replace />} />
                <Route path="/stores/:id" element={<StoresIdRedirect />} />
                <Route path="/stores/:id/edit" element={<StoresIdRedirect suffix="/edit" />} />
                <Route path="/stores/:id/catalog" element={<StoresIdRedirect suffix="/catalog" />} />

                {/* Store - Inventory Report */}
                <Route path="/store/inventory-report" element={<InventoryReport />} />
                {/* Alias: /reports/inventory -> /store/inventory-report (redirect para URL canônica) */}
                <Route path="/reports/inventory" element={<Navigate to="/store/inventory-report" replace />} />

                {/* Cart & Checkout */}
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />

                {/* Sales */}
                <Route path="/sales" element={<SalesListPage />} />
                <Route path="/sales/:id" element={<SaleDetailPage />} />

                {/* Store Settings (admin) */}
                <Route path="/settings/payment-methods" element={<PaymentMethodsPage />} />
                <Route path="/settings/shipment-methods" element={<ShipmentMethodsPage />} />

                {/* Tracking (public-like but protected) */}
                <Route path="/tracking" element={<TrackingPage />} />
                <Route path="/tracking/:code" element={<TrackingPage />} />

                {/* Campaign routes (Marketing Campaigns) */}
                <Route path="/campaign" element={<CampaignList />} />
                <Route path="/campaign/create" element={<CampaignCreate />} />
                <Route path="/campaign/wizard" element={<CampaignWizard />} />
                <Route path="/campaign/wizard/:id" element={<CampaignWizard />} />
                <Route path="/campaign/dashboard" element={<CampaignDashboard />} />
                <Route path="/campaign/attributes" element={<CampaignAttributes />} />
                <Route path="/campaign/attributes/create" element={<CampaignAttributeForm />} />
                <Route path="/campaign/attributes/:id/edit" element={<CampaignAttributeForm />} />
                <Route path="/campaign/attributes/:attributeId/options" element={<CampaignAttributeOptions />} />
                <Route path="/campaign/attribute-types" element={<CampaignAttributeTypes />} />
                <Route path="/campaign/attribute-types/create" element={<CampaignAttributeTypeForm />} />
                <Route path="/campaign/attribute-types/:id/edit" element={<CampaignAttributeTypeForm />} />
                <Route path="/campaign/jobs" element={<CampaignJobs />} />
                <Route path="/campaign/import-jobs" element={<CampaignImportJobs />} />
                <Route path="/campaign/terms" element={<CampaignTerms />} />
                <Route path="/campaign/results" element={<CampaignResults />} />
                <Route path="/campaign/credits" element={<CampaignCredits />} />
                <Route path="/campaign/memberships" element={<MembershipList />} />
                <Route path="/campaign/memberships/create" element={<MembershipCreate />} />
                <Route path="/campaign/modalities" element={<CampaignModalities />} />
                <Route path="/campaign/modalities/create" element={<CampaignModalityForm />} />
                <Route path="/campaign/modalities/:id" element={<CampaignModalityForm />} />
                <Route path="/campaign/modalities/:id/edit" element={<CampaignModalityForm />} />
                {/* Campaign Performance routes */}
                <Route path="/campaign/performance" element={<CampaignPerformanceDashboard />} />
                <Route path="/campaign/performance/config" element={<CampaignPerformanceConfig />} />
                <Route path="/campaign/performance/results" element={<CampaignPerformanceResults />} />
                {/* Campaign Participants Report */}
                <Route path="/campaign/participants" element={<ParticipantsReport />} />
                {/* Score Lot Reversal */}
                <Route path="/campaign/score-lots/reversal" element={<ScoreLotReversal />} />
                {/* Legacy Yii URL for Score Lot Reversal */}
                <Route path="/score/scoreCredit/listLot" element={<ScoreLotReversal />} />
                {/* Score Credit Generation (batch) -- TMF9-58528 */}
                <Route path="/campaign/score-lots/generate-credits" element={<ScoreCreditGeneration />} />
                {/* Score Result Generation (batch) -- TMF9-58526 */}
                <Route path="/campaign/score-lots/generate-results" element={<ScoreResultGeneration />} />
                <Route path="/campaign/:id/performance" element={<CampaignPerformanceDashboard />} />
                <Route path="/campaign/:id" element={<CampaignDetail />} />
                <Route path="/campaign/:id/edit" element={<CampaignUpdate />} />

                {/* Ranking routes */}
                <Route path="/ranking" element={<RankingListPage />} />
                <Route path="/ranking/create" element={<RankingCreatePage />} />
                <Route path="/ranking/generate" element={<RankingGenerate />} />
                <Route path="/ranking/jobs" element={<RankingJobs />} />
                <Route path="/ranking/:id" element={<RankingDetailPage />} />
                <Route path="/ranking/:id/edit" element={<RankingEditPage />} />

                {/* Analytics routes (Superset) */}
                <Route path="/analytics" element={<SupersetPage />} />

                {/* Reports routes */}
                <Route path="/reports" element={<ReportsDashboard />} />
                <Route path="/reports/manage" element={<ReportsList />} />
                <Route path="/reports/view/:id" element={<ReportView />} />
                <Route path="/reports/builder" element={<ReportBuilder />} />
                <Route path="/reports/builder/:id" element={<ReportBuilder />} />
                <Route path="/reports/access-log" element={<AccessLogReport />} />
                <Route path="/reports/content-access" element={<ContentAccessReport />} />
                <Route path="/reports/audit-log" element={<AuditLogReport />} />
                <Route path="/reports/campaign-data" element={<CampaignDataReport />} />
                <Route path="/reports/campaign-export" element={<CampaignExportReport />} />
                {/* Legacy redirect - participants moved to /campaign/participants */}
                <Route path="/reports/participants" element={<Navigate to="/campaign/participants" replace />} />
                <Route path="/reports/sales" element={<SalesReport />} />
                <Route path="/reports/person-data" element={<PersonDataReport />} />
                <Route path="/reports/person-hierarchy" element={<PersonHierarchyReport />} />
                <Route path="/reports/person-segments" element={<PersonSegmentsReport />} />
                <Route path="/reports/products" element={<ProductsReport />} />
                <Route path="/reports/product-sales" element={<ProductSalesReport />} />
                <Route path="/reports/budget" element={<Navigate to="/budget/report" replace />} />
                <Route path="/reports/cost-centers" element={<CostCentersReport />} />
                <Route path="/reports/transactions" element={<TransactionsReport />} />

                {/* Quiz routes (Avaliações) */}
                <Route path="/quiz" element={<QuizList />} />
                <Route path="/quiz/create" element={<QuizCreate />} />
                <Route path="/quiz/config" element={<QuizConfig />} />
                <Route path="/quiz/executions" element={<QuizExecutions />} />
                <Route path="/quiz/answers" element={<QuizAnswers />} />
                <Route path="/quiz/reports" element={<QuizReports />} />
                <Route path="/quiz/reports/answers" element={<QuizAnswersReport />} />
                <Route path="/quiz/reports/person" element={<QuizPersonReport />} />
                <Route path="/quiz/:id" element={<QuizDetail />} />
                <Route path="/quiz/:id/edit" element={<QuizUpdate />} />
                <Route path="/quiz/:id/answer" element={<QuizAnswer />} />

                {/* Legacy Yii URLs for Quiz (redirects) */}
                <Route path="/menuQuiz" element={<Navigate to="/quiz" replace />} />
                <Route path="/ManageQuiz" element={<Navigate to="/quiz" replace />} />
                <Route path="/ReportQuiz" element={<Navigate to="/quiz/reports" replace />} />
                <Route path="/QuizAnswerValue" element={<Navigate to="/quiz/answers" replace />} />
                {/* Legacy Yii URLs for Avaliação/Certificação (redirects to Quiz) */}
                <Route path="/menuCertification" element={<Navigate to="/quiz" replace />} />
                <Route path="/ManageTestConfig" element={<Navigate to="/quiz/config" replace />} />
                <Route path="/ManageTest" element={<Navigate to="/quiz" replace />} />
                <Route path="/ManageExecution" element={<Navigate to="/quiz/executions" replace />} />
                <Route path="/IndexError" element={<Navigate to="/quiz/reports" replace />} />
                <Route path="/TestReportErrorByCategory" element={<Navigate to="/quiz/reports" replace />} />
                <Route path="/TestReportQuestionByPeople" element={<Navigate to="/quiz/reports" replace />} />
                <Route path="/TestReport" element={<Navigate to="/quiz/reports" replace />} />

                {/* Function routes (Funções serverless) */}
                <Route path="/function" element={<FunctionList />} />
                <Route path="/function/create" element={<FunctionCreate />} />
                <Route path="/function/:id" element={<FunctionDetail />} />

                {/* Service Desk — Central de Atendimento. Rota canônica consolidada em
                    /service-desk (TMF9-58757/58758): antes havia duas montagens paralelas
                    do <ServiceDeskApp> (/sac via proxy Hub, /atendimento via motor SD local
                    Modelo A) — o motor local venceu (alinhado ao padrão-alvo da epic TH-58720,
                    mesmo client-per-produto usado por DANI/HelpCor). /sac e /atendimento agora
                    são redirects de compatibilidade. */}
                <Route path="/service-desk" element={<ServiceDeskPage />} />
                <Route path="/service-desk/*" element={<ServiceDeskPage />} />

                {/* Redirects de compatibilidade → /service-desk (TMF9-58757/58758).
                    As superfícies SAC legadas via proxy Hub (chat/relatórios/legacy-kanban,
                    antes gated para copa) foram removidas — o motor SD local (@tmf/service-desk-front)
                    cobre todos os tenants em /service-desk. */}
                <Route path="/sac" element={<Navigate to="/service-desk" replace />} />
                <Route path="/sac/*" element={<Navigate to="/service-desk" replace />} />
                <Route path="/atendimento" element={<Navigate to="/service-desk" replace />} />
                <Route path="/atendimento/*" element={<Navigate to="/service-desk" replace />} />
                <Route path="/contact" element={<Navigate to="/service-desk" replace />} />

                {/* Legacy Yii URLs for Contact (motor removido — TH-56761) → Service Desk */}
                <Route path="/ManageContact" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManageContactAttribute" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManageContactAttributeType" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManageStatus" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManagePriority" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManageCategory" element={<Navigate to="/service-desk" replace />} />
                <Route path="/ManageChannel" element={<Navigate to="/service-desk" replace />} />

                {/* Comunicação: motor removido (TH-56761) — funcionalidade volta via @tmf/communication-front em /comunicacao */}
                <Route path="/communication/*" element={<Navigate to="/comunicacao" replace />} />

                {/* Import routes (Importações) */}
                <Route path="/import" element={<ImportDashboard />} />
                <Route path="/import/upload" element={<ImportUpload />} />
                <Route path="/import/files" element={<ImportFiles />} />
                <Route path="/import/logs" element={<ImportLogs />} />
                <Route path="/import/types" element={<ImportTypes />} />
                <Route path="/import/types/create" element={<ImportTypeCreate />} />
                <Route path="/import/types/:id" element={<ImportTypeUpdate />} />
                <Route path="/import/types/:id/edit" element={<ImportTypeUpdate />} />
                <Route path="/import/validations" element={<ImportValidations />} />
                <Route path="/import/validations/create" element={<ImportValidationCreate />} />
                <Route path="/import/validations/:id" element={<ImportValidationDetail />} />
                <Route path="/import/validations/:id/edit" element={<ImportValidationUpdate />} />
                <Route path="/import/conversions" element={<ImportConversions />} />
                <Route path="/import/conversions/create" element={<ImportConversionCreate />} />
                <Route path="/import/conversions/:id/edit" element={<ImportConversionUpdate />} />
                <Route path="/import/fields" element={<ImportFields />} />
                <Route path="/import/fields/create" element={<ImportFieldCreate />} />
                <Route path="/import/fields/:id/edit" element={<ImportFieldUpdate />} />
                <Route path="/import/config/create" element={<ImportConfigCreate />} />

                {/* Legacy Yii URLs for Import (redirects) */}
                <Route path="/Importations" element={<Navigate to="/import" replace />} />
                <Route path="/ImportFile" element={<Navigate to="/import/upload" replace />} />
                <Route path="/ManageImport" element={<Navigate to="/import" replace />} />
                <Route path="/ShowImportLogs" element={<Navigate to="/import/logs" replace />} />
                <Route path="/ManageImportType" element={<Navigate to="/import/types" replace />} />
                <Route path="/ManageImportValidation" element={<Navigate to="/import/validations" replace />} />
                <Route path="/ManageImportFields" element={<Navigate to="/import/fields" replace />} />
                <Route path="/ManageImportConvert" element={<Navigate to="/import/conversions" replace />} />
                <Route path="/ConfigurationsImport" element={<Navigate to="/import/types" replace />} />
                <Route path="/ManageImportJob" element={<Navigate to="/import/logs" replace />} />

                {/* Drawing routes (Sorteios) */}
                <Route path="/drawing" element={<DrawingList />} />
                <Route path="/drawing/results" element={<DrawingResults />} />

                {/* Marketing routes (Social Posts, YouTube, Calendar) */}
                <Route path="/marketing/social-posts" element={<SocialPostPage />} />
                <Route path="/marketing/youtube" element={<YouTubeManager />} />
                <Route path="/marketing/calendar" element={<EditorialCalendar />} />

                {/* Legacy module routes (em migracao) */}
                <Route path="/score" element={<LegacyModulePage />} />
                <Route path="/term" element={<LegacyModulePage />} />

                {/* Checkin routes (Mapas e visualizações) */}
                <Route path="/checkin/map" element={<CheckinMapPage />} />
                <Route path="/checkin/plan" element={<CheckinPlanPage />} />

                {/* Lambda/Functions: gerenciado no Hub (Application > Funções) */}

                {/* Test pages (Development) */}
                <Route path="/test/charts" element={<ChartTestPage />} />

                {/* Design System Catalog (superadmin only) */}
                <Route path="/design-system" element={<ProtectedRoute requiredRole="superadmin"><DesignSystemCatalog /></ProtectedRoute>} />

                {/* Docs pages (Markdown Notebook) */}
                <Route path="/docs" element={<DocsContentPage />} />
                <Route path="/docs/:fileId" element={<DocsContentPage />} />
                <Route path="/docs-local" element={<DocsPage />} />
                <Route path="/docs-local/:fileId" element={<DocsPage />} />

                {/* File Manager pages */}
                <Route path="/files" element={<FilesPage />} />
                <Route path="/files/:folderId" element={<FilesPage />} />

                {/* Legacy Yii URL redirects (menu aliases) */}
                <Route path="/ContentManager" element={<Navigate to="/content" replace />} />
                <Route path="/PersonManager" element={<Navigate to="/person" replace />} />
                <Route path="/ProductManager" element={<Navigate to="/product" replace />} />
                <Route path="/ResourceManager" element={<Navigate to="/resource" replace />} />
                <Route path="/ContactManager" element={<Navigate to="/service-desk" replace />} />

                {/* Currency Yii URL redirects */}
                <Route path="/ManageCurrencyRate" element={<Navigate to="/settings/currencies" replace />} />
                <Route path="/ManageCurrency" element={<Navigate to="/settings/currencies" replace />} />
                <Route path="/currency/currency" element={<Navigate to="/settings/currencies" replace />} />
                <Route path="/currency/currency/admin" element={<Navigate to="/settings/currencies" replace />} />
                <Route path="/currency/currencyrate" element={<Navigate to="/settings/currencies" replace />} />
                <Route path="/currency/currencyrate/admin" element={<Navigate to="/settings/currencies" replace />} />

                {/* Settings redirects */}
                <Route path="/settings" element={<Navigate to="/settings/etl" replace />} />

                {/* ETL Yii URL redirects */}
                <Route path="/etl" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/Etl" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/EtlList" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/etl/etl" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/etl/etl/admin" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/etl/etl/index" element={<Navigate to="/settings/etl" replace />} />
                <Route path="/ManageEtl" element={<Navigate to="/settings/etl" replace />} />

                {/* Legacy Yii menu aliases - Redirects adicionais */}
                {/* Person & Registration */}
                <Route path="/ManagePerson" element={<Navigate to="/person" replace />} />
                <Route path="/ManageIndividualPerson" element={<Navigate to="/person" replace />} />
                <Route path="/ManagePersonType" element={<Navigate to="/settings/person-types" replace />} />
                <Route path="/ManagePhoneType" element={<Navigate to="/settings/phone-types" replace />} />
                <Route path="/ManageEmailType" element={<Navigate to="/settings/email-types" replace />} />
                <Route path="/ManageAddresType" element={<Navigate to="/settings/address-types" replace />} />
                <Route path="/PersonDataReport" element={<Navigate to="/reports/person-data" replace />} />

                {/* Segmentation */}
                <Route path="/ManageRegion" element={<Navigate to="/segmentation/regions" replace />} />
                <Route path="/ManageSegment" element={<Navigate to="/segmentation/segments" replace />} />

                {/* Campaign */}
                <Route path="/ManageCampaign" element={<Navigate to="/campaign" replace />} />
                <Route path="/ManageCampaignAttribute" element={<Navigate to="/campaign/attributes" replace />} />
                <Route path="/ManageCampaignJob" element={<Navigate to="/campaign/jobs" replace />} />
                <Route path="/ManageCampaignImportJob" element={<Navigate to="/campaign/import-jobs" replace />} />
                <Route path="/ManageModality" element={<Navigate to="/campaign/modalities" replace />} />
                <Route path="/ManageCredit" element={<Navigate to="/campaign/credits" replace />} />
                <Route path="/ManageResult" element={<Navigate to="/campaign/results" replace />} />
                <Route path="/ManageTerm" element={<Navigate to="/campaign/terms" replace />} />
                <Route path="/ManageTermAttribute" element={<Navigate to="/campaign/attributes" replace />} />
                <Route path="/ManageTermAttributeType" element={<Navigate to="/campaign/attribute-types" replace />} />
                <Route path="/ManageVariableType" element={<Navigate to="/campaign/attribute-types" replace />} />
                <Route path="/ManageScoreConfig" element={<Navigate to="/campaign/performance/config" replace />} />
                <Route path="/DashboardCampaign" element={<Navigate to="/campaign/dashboard" replace />} />
                <Route path="/ImportCampaign" element={<Navigate to="/campaign/import-jobs" replace />} />
                <Route path="/ExportCampaignData" element={<Navigate to="/reports/campaign-export" replace />} />

                {/* Ranking */}
                <Route path="/ManageRanking" element={<Navigate to="/ranking" replace />} />
                <Route path="/ManageRankingJob" element={<Navigate to="/ranking/jobs" replace />} />

                {/* Resource */}
                <Route path="/ManageResourceAttribute" element={<Navigate to="/resource/attributes" replace />} />
                <Route path="/ResourceCheckinManage" element={<Navigate to="/resource/checkins" replace />} />

                {/* Product */}
                <Route path="/ManageProductBrand" element={<Navigate to="/product/brands" replace />} />
                <Route path="/ManageProductFamily" element={<Navigate to="/product/families" replace />} />
                <Route path="/ManageProductImage" element={<Navigate to="/product/images" replace />} />
                <Route path="/importProductImage" element={<Navigate to="/product/import-images" replace />} />
                <Route path="/ExportImagemProductCustom" element={<Navigate to="/product/export-images" replace />} />
                <Route path="/ImportListProductAndExportImageFile" element={<Navigate to="/product/import-images" replace />} />
                <Route path="/newManageProducts" element={<Navigate to="/product" replace />} />
                <Route path="/newManageProductCategory" element={<Navigate to="/product/categories" replace />} />
                <Route path="/newManageProductType" element={<Navigate to="/product/types" replace />} />
                <Route path="/newManageProductAttr" element={<Navigate to="/product/attributes" replace />} />
                <Route path="/newManageAttrProductTypeStore" element={<Navigate to="/product/attributes" replace />} />
                <Route path="/newManageConfigProducts" element={<Navigate to="/product" replace />} />

                {/* Store */}
                <Route path="/newManageStore" element={<Navigate to="/store" replace />} />
                <Route path="/newManageSales" element={<Navigate to="/sales" replace />} />
                <Route path="/newManagePaymentMethod" element={<Navigate to="/settings/payment-methods" replace />} />
                <Route path="/newManageShipmentMethod" element={<Navigate to="/settings/shipment-methods" replace />} />

                {/* Reports */}
                <Route path="/Reports" element={<Navigate to="/reports" replace />} />
                <Route path="/manageReportMkf" element={<Navigate to="/reports" replace />} />
                <Route path="/LoginAccessReport" element={<Navigate to="/reports/access-log" replace />} />
                <Route path="/LoginContentAccessReport" element={<Navigate to="/reports/content-access" replace />} />
                <Route path="/ExportHierarchical" element={<Navigate to="/reports/person-hierarchy" replace />} />
                <Route path="/report-client" element={<Navigate to="/reports/person-data" replace />} />

                {/* Budget */}
                <Route path="/ListActions" element={<Navigate to="/budget/actions" replace />} />

                {/* Drawing (Sorteios) */}
                <Route path="/manageRaffles" element={<Navigate to="/drawing" replace />} />
                <Route path="/manageRaffleResults" element={<Navigate to="/drawing/results" replace />} />

                {/* Settings & System */}
                <Route path="/ManageMessages" element={<Navigate to="/notifications" replace />} />
                <Route path="/fullConfigurations" element={<Navigate to="/settings" replace />} />
                <Route path="/FileManager" element={<Navigate to="/files" replace />} />
                <Route path="/DashboardCheckin" element={<Navigate to="/checkin/map" replace />} />

                {/* Hub do Cliente routes */}
                <Route path="/hub/dashboard" element={<HubDashboard />} />
                <Route path="/hub/tickets" element={<HubDemandList />} />
                <Route path="/hub/tickets/new" element={<HubTicketNew />} />
                <Route path="/hub/tickets/metrics" element={<HubTicketMetrics />} />
                <Route path="/hub/tickets/:id" element={<HubDemandDetail />} />
                <Route path="/hub/invoices" element={<HubInvoiceList />} />
                <Route path="/hub/contracts" element={<HubContractList />} />
                <Route path="/hub/hours" element={<HubHoursList />} />
                <Route path="/hub/consumption" element={<HubConsumption />} />
                <Route path="/hub/documents" element={<HubDocuments />} />
                {/* [TH-55603] Comunicação agora vem do pacote @tmf/communication-front (consome o Hub via proxy). */}
                {/* [TMF9-60345] Sub-rota `:section` mapeia para a prop `section` do CommunicationApp
                    (painel/campanhas/templates/financeiro/conciliacao/configuracoes) — deep-link e
                    refresh preservam a seção. `/comunicacao` (sem param) abre no painel (home de produto). */}
                <Route path="/comunicacao" element={<CommunicationPage />} />
                <Route path="/comunicacao/:section" element={<CommunicationPage />} />
                {/* Rotas legadas → nova central de Comunicação (preserva bookmarks). */}
                <Route path="/hub/campaigns" element={<Navigate to="/comunicacao" replace />} />
                <Route path="/hub/messaging" element={<Navigate to="/comunicacao" replace />} />
                <Route path="/mensageria" element={<Navigate to="/comunicacao" replace />} />
                <Route path="/hub/whatsapp/*" element={<Navigate to="/comunicacao" replace />} />

                {/* Smart Inbox routes (Upload inteligente de documentos) */}
                <Route path="/smart-inbox/review/:id" element={<SmartInboxReviewPage />} />

                {/* Blog routes (Editor WYSIWYG) */}
                <Route path="/blog/editor" element={<BlogEditorPage />} />
                <Route path="/blog/editor/:id" element={<BlogEditorPage />} />

                {/* Client-specific routes (overlay) */}
                {clientRouteElements}

                {/* Catch-all for dynamic content pages by code/alias */}
                {/* URLs como /copagaz_promo, /liquigas_promo, etc. */}
                <Route path="/:code" element={<ContentView />} />

                {/* 404 - dentro do layout para manter header/footer */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
              </ClientConfigInitializer>
              </ThemeAuthSync>
              </ConfirmProvider>
        </AuthProvider>
          </ConfirmProvider>
      </FragmentModeProvider>
    </I18nProvider>
      </ToastProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

// Wrapper que inclui auto-report
function AppWithAutoReport() {
  return (
    <AutoReportProvider>
      <App />
    </AutoReportProvider>
  );
}

export default AppWithAutoReport;
